import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Bell } from 'lucide-react';
import NotificationDropdown from './NotificationDropdown';
import { subscribeToNotifications, getUnreadCount } from './notificationService';
import { getWatchedAddresses } from './userService';

const NotificationBell = ({ user }) => {
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [watchedAddresses, setWatchedAddresses] = useState([]);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  // Fetch watched addresses when user connects
  useEffect(() => {
    if (!user) {
      setWatchedAddresses([]);
      return;
    }

    const fetchWatchedAddresses = async () => {
      const watched = await getWatchedAddresses();
      setWatchedAddresses(watched);
    };

    fetchWatchedAddresses();
  }, [user]);

  // Subscribe to notifications (including watched addresses)
  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    const unsubscribe = subscribeToNotifications(user.address, watchedAddresses, (notifs) => {
      setNotifications(notifs);
      setUnreadCount(getUnreadCount(notifs));
    });

    return () => unsubscribe();
  }, [user, watchedAddresses]);

  // Calculate dropdown position when showing
  useEffect(() => {
    if (showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownWidth = 384; // w-96 = 384px
      const margin = 8; // Minimum margin from screen edges

      // Calculate default right position
      let rightPos = window.innerWidth - rect.right;

      // Check if dropdown would extend off left edge
      const leftEdge = window.innerWidth - rightPos - dropdownWidth;

      if (leftEdge < margin) {
        // Dropdown would go off left edge
        // Position it with margin from right edge instead
        rightPos = margin;
      }

      // Also check if dropdown would extend off right edge
      if (rightPos < margin) {
        rightPos = margin;
      }

      setDropdownPosition({
        top: rect.bottom + 8, // 8px below button
        right: rightPos
      });
    }
  }, [showDropdown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        buttonRef.current && !buttonRef.current.contains(event.target) &&
        dropdownRef.current && !dropdownRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showDropdown]);

  // Don't show bell if user not connected
  if (!user) {
    return null;
  }

  return (
    <>
      {/* Bell Icon Button */}
      <button
        ref={buttonRef}
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5" />

        {/* Badge for unread count */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown rendered via portal */}
      {showDropdown && createPortal(
        <div
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            right: `${dropdownPosition.right}px`,
            zIndex: 9999,
          }}
        >
          <NotificationDropdown
            notifications={notifications}
            onClose={() => setShowDropdown(false)}
            user={user}
          />
        </div>,
        document.body
      )}
    </>
  );
};

export default NotificationBell;
